import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  HeartIcon, InfoIcon, CheckIcon, SparkleIcon, StarIcon
} from './BespokeIcons';

/**
 * SoulScoreWidget - Track well-being through a beautiful tap interface
 * 
 * Users tap 1-10 times, each tap plays an alluring chime
 * Used at checkpoints: start of session, after Belonging, after Vision, after Story
 */

// Audio context for creating beautiful chimes
const createChimeSound = (tapNumber, maxTaps = 10) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    // Fun video game-style ascending chime - bright and pleasing
    // Uses pentatonic scale notes for guaranteed pleasant sound
    const pentatonicNotes = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25, 587.33, 659.25, 783.99, 880.00]; // C major pentatonic
    const baseFreq = pentatonicNotes[tapNumber - 1] || pentatonicNotes[9];
    
    const createOsc = (freq, type, gain, delay = 0, duration = 0.3) => {
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      osc.type = type;
      osc.frequency.setValueAtTime(freq, audioCtx.currentTime + delay);
      
      // Snappy video game envelope - quick attack, medium decay
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime + delay);
      gainNode.gain.linearRampToValueAtTime(gain, audioCtx.currentTime + delay + 0.01); // Fast attack
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + delay + duration);
      
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      osc.start(audioCtx.currentTime + delay);
      osc.stop(audioCtx.currentTime + delay + duration + 0.1);
    };
    
    // Main bright tone (sine for clarity)
    createOsc(baseFreq, 'sine', 0.3, 0, 0.25);
    // Octave sparkle for brightness
    createOsc(baseFreq * 2, 'sine', 0.15, 0.01, 0.2);
    // Fifth harmony for richness
    createOsc(baseFreq * 1.5, 'triangle', 0.08, 0.02, 0.15);
    
    // Extra sparkle on higher scores - add ascending arpeggio
    if (tapNumber >= 6) {
      createOsc(baseFreq * 2.5, 'sine', 0.05, 0.05, 0.15);
    }
    if (tapNumber >= 8) {
      createOsc(baseFreq * 3, 'sine', 0.04, 0.07, 0.12);
    }
    
    // Victory fanfare on max score!
    if (tapNumber === maxTaps) {
      setTimeout(() => {
        // Celebratory arpeggio
        createOsc(523.25, 'sine', 0.2, 0, 0.15);     // C5
        createOsc(659.25, 'sine', 0.2, 0.08, 0.15);  // E5
        createOsc(783.99, 'sine', 0.2, 0.16, 0.15);  // G5
        createOsc(1046.50, 'sine', 0.25, 0.24, 0.4); // C6 - hold longer
      }, 150);
    }
  } catch (e) {
    console.log('Audio not available');
  }
};

// Encouraging messages based on score
const getEncouragingMessage = (score) => {
  if (score === 0) return "Tap to share how you're feeling...";
  if (score <= 2) return "Thank you for being honest 💙";
  if (score <= 4) return "Every feeling is valid here 🌱";
  if (score <= 6) return "You're right where you need to be ✨";
  if (score <= 8) return "Beautiful energy! 🌟";
  if (score <= 9) return "Wonderful! Keep shining ☀️";
  return "Radiant! 🌈✨";
};

// Visual descriptions
const getScoreDescription = (score) => {
  if (score === 0) return "";
  if (score <= 2) return "Low";
  if (score <= 4) return "Moderate";
  if (score <= 6) return "Good";
  if (score <= 8) return "Great";
  return "Excellent";
};

const SoulScoreWidget = ({
  checkpoint, // 'timeZero' | 'belonging' | 'vision' | 'story'
  userId,
  sessionId,
  onComplete, // Called when score is submitted
  onSkip, // Optional skip callback
  isOpen = true,
  onClose
}) => {
  const [score, setScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [ripples, setRipples] = useState([]);
  
  // Handle tap
  const handleTap = useCallback(() => {
    if (score < 10) {
      const newScore = score + 1;
      setScore(newScore);
      createChimeSound(newScore);
      
      // Add visual ripple
      const rippleId = Date.now();
      setRipples(prev => [...prev, rippleId]);
      setTimeout(() => {
        setRipples(prev => prev.filter(id => id !== rippleId));
      }, 1000);
    }
  }, [score]);
  
  // Reset score
  const handleReset = () => {
    setScore(0);
  };
  
  // Submit score
  const handleSubmit = async () => {
    if (score === 0) {
      toast.error('Please tap at least once to share your score');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/soul-score/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userId || 'anonymous',
          sessionId: sessionId || `session-${Date.now()}`,
          checkpoint,
          score,
          timestamp: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        // Play success sound
        createChimeSound(10);
        setTimeout(() => createChimeSound(8), 200);
        
        toast.success('Thank you for sharing! 💫');
        
        if (onComplete) {
          onComplete({ checkpoint, score });
        }
      } else {
        // Still call onComplete even if save fails
        if (onComplete) {
          onComplete({ checkpoint, score });
        }
      }
    } catch (error) {
      console.error('Failed to save soul score:', error);
      // Still proceed even if save fails
      if (onComplete) {
        onComplete({ checkpoint, score });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Checkpoint labels
  const checkpointLabels = {
    timeZero: "Before We Begin",
    belonging: "After Your Belonging Practice",
    vision: "After Vision Quest",
    story: "After Your Story"
  };
  
  const checkpointDescriptions = {
    timeZero: "How are you feeling right now, as you begin?",
    belonging: "How do you feel after exploring your feelings and needs?",
    vision: "How do you feel after clarifying your vision?",
    story: "How do you feel after experiencing your story?"
  };
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-gradient-to-br from-zinc-900 via-indigo-900/30 to-zinc-900 border-indigo-500/30 text-white max-w-md p-0 overflow-hidden">
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-white/10">
            <DialogHeader>
              <DialogTitle className="text-xl font-light text-center flex items-center justify-center gap-2">
                <HeartIcon size={20} color="#F472B6" />
                Soul Score
                <button 
                  onClick={() => setShowInfo(true)}
                  className="ml-2 p-1 rounded-full hover:bg-white/10 transition-colors"
                >
                  <InfoIcon size={14} color="#818CF8" />
                </button>
              </DialogTitle>
              <DialogDescription className="text-center text-white/60 mt-2">
                {checkpointLabels[checkpoint] || "Check In"}
              </DialogDescription>
            </DialogHeader>
          </div>
          
          {/* Main content */}
          <div className="p-6 pt-4">
            <p className="text-center text-white/70 mb-6 text-sm">
              {checkpointDescriptions[checkpoint] || "How are you feeling?"}
            </p>
            
            {/* Tap area */}
            <div className="relative flex flex-col items-center mb-6">
              {/* The tappable orb */}
              <button
                onClick={handleTap}
                disabled={score >= 10}
                className={`relative w-40 h-40 rounded-full transition-all duration-300 transform 
                  ${score >= 10 ? 'cursor-default' : 'cursor-pointer hover:scale-105 active:scale-95'}
                  focus:outline-none focus:ring-2 focus:ring-indigo-500/50`}
                style={{
                  background: `radial-gradient(circle at 30% 30%, 
                    hsl(${280 + score * 8}, 70%, ${35 + score * 3}%) 0%, 
                    hsl(${260 + score * 10}, 60%, ${20 + score * 2}%) 100%)`
                }}
                data-testid="soul-score-tap-button"
              >
                {/* Inner glow */}
                <div 
                  className="absolute inset-4 rounded-full opacity-50"
                  style={{
                    background: `radial-gradient(circle, 
                      rgba(255,255,255,${0.1 + score * 0.03}) 0%, 
                      transparent 70%)`
                  }}
                />
                
                {/* Score display */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-5xl font-light text-white/90" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {score}
                  </span>
                  <span className="text-xs text-white/50 mt-1">
                    {score < 10 ? 'tap to increase' : 'max reached'}
                  </span>
                </div>
                
                {/* Ripple effects */}
                {ripples.map(rippleId => (
                  <div
                    key={rippleId}
                    className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"
                    style={{ animationDuration: '1s' }}
                  />
                ))}
              </button>
              
              {/* Score indicator dots */}
              <div className="flex gap-1.5 mt-4">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i < score 
                        ? 'bg-gradient-to-r from-indigo-400 to-pink-400 scale-110' 
                        : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
              
              {/* Description */}
              <p className="text-center text-white/50 text-xs mt-2">
                {getScoreDescription(score)}
              </p>
            </div>
            
            {/* Encouraging message */}
            <p className="text-center text-white/70 mb-6 min-h-[24px] transition-all duration-300">
              {getEncouragingMessage(score)}
            </p>
            
            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleSubmit}
                disabled={score === 0 || isSubmitting}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white py-5"
                data-testid="soul-score-submit-btn"
              >
                {isSubmitting ? (
                  <SparkleIcon size={16} color="#FFF" className="mr-2 animate-spin" />
                ) : (
                  <CheckIcon size={16} color="#FFF" className="mr-2" />
                )}
                {isSubmitting ? 'Saving...' : 'Continue'}
              </Button>
              
              <div className="flex gap-2">
                {score > 0 && (
                  <Button
                    onClick={handleReset}
                    variant="ghost"
                    className="flex-1 text-white/50 hover:text-white hover:bg-white/10"
                  >
                    Reset
                  </Button>
                )}
                
                {onSkip && (
                  <Button
                    onClick={onSkip}
                    variant="ghost"
                    className="flex-1 text-white/40 hover:text-white/60"
                  >
                    Skip for now
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Info Dialog */}
      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className="bg-zinc-900 border-indigo-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-indigo-400 flex items-center gap-2">
              <HeartIcon size={20} color="#F472B6" />
              What is Soul Score?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-white/80 text-sm">
            <p>
              Soul Score is a simple way to check in with yourself at key moments during your Belonging practice.
            </p>
            <p>
              <strong className="text-indigo-300">How it works:</strong> Tap the orb 1-10 times to indicate how you're feeling. 
              Each tap plays a gentle chime and increases your score.
            </p>
            <div className="bg-white/5 rounded-lg p-4 space-y-2">
              <p className="text-white/60 text-xs">Score Guide:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span>1-2: Low energy/struggling</span>
                <span>3-4: Moderate/neutral</span>
                <span>5-6: Good/content</span>
                <span>7-8: Great/positive</span>
                <span className="col-span-2">9-10: Excellent/thriving</span>
              </div>
            </div>
            <p>
              <strong className="text-indigo-300">Why we ask:</strong> Tracking changes in how you feel helps 
              us understand the impact of these practices. Your data is anonymized and used only for research 
              to improve the Belonging Sessions experience.
            </p>
            <p className="text-white/50 text-xs italic">
              This is completely optional and you can skip at any time.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SoulScoreWidget;
